/**
 * AchievementsView — gamification. EP / level / streak / achievements.
 * Per app-codex: surface "achievements.list". Behaviour: gamification.view.
 * Per app-recipe/ETHOS.md §0.7: 10 renamed achievements, 10 parable level names.
 * Dev mode: uses devDb.
 */
import { useEffect, useState } from 'react'
import { fetchGamification, LEVEL_NAMES, LEVEL_THRESHOLDS, type DbGamification } from '../db'
import { devDb } from '../devMode'
import { useApp } from '../store'
import { Award, Sparkles } from 'lucide-react'

export const ACHIEVEMENTS = [
  { id: 'first-step', label: 'First Step', description: 'Complete your first task', ep: 0 },
  { id: 'week-of-fruit', label: 'Week of Fruit', description: 'A week of steady tending', ep: 100 },
  { id: 'month-of-light', label: 'Month of Light', description: 'A month of faithfulness', ep: 250 },
  { id: 'faithful-helper', label: 'Faithful Helper', description: 'Help without being asked', ep: 500 },
  { id: 'morning-watcher', label: 'Morning Watcher', description: 'A week of early starts', ep: 100 },
  { id: 'kind-spirit', label: 'Kind Spirit', description: 'A kind word at the right time', ep: 250 },
  { id: 'patient-sower', label: 'Patient Sower', description: 'Tend a long project to harvest', ep: 1000 },
  { id: 'steady-steward', label: 'Steady Steward', description: 'A year of faithfulness', ep: 4000 },
  { id: 'pillar-of-home', label: 'Pillar of Home', description: 'Carry the family through a hard season', ep: 6000 },
  { id: 'elder-of-the-house', label: 'Elder of the House', description: 'A lifetime of quiet faithfulness', ep: 10000 },
] as const

export function AchievementsView() {
  const { currentMember, dev } = useApp()
  const [gamification, setGamification] = useState<DbGamification | null>(null)

  useEffect(() => {
    if (!currentMember) return
    const load = dev
      ? devDb.gamification.get(currentMember.id)
      : fetchGamification(currentMember.id)
    load.then(({ data }) => setGamification(data as any))
  }, [currentMember, dev])

  const ep = (gamification as any)?.ep ?? 0
  const level = (gamification as any)?.level ?? 1
  const streakDays = (gamification as any)?.streak_days ?? 0
  const levelName = LEVEL_NAMES[level - 1] ?? 'Sower'
  const levelEp = LEVEL_THRESHOLDS[level - 1] ?? 0
  const nextLevelEp = LEVEL_THRESHOLDS[level] ?? 10000
  const progress = nextLevelEp > levelEp ? ((ep - levelEp) / (nextLevelEp - levelEp)) * 100 : 100
  const earned = new Set<string>(((gamification as any)?.achievements ?? []) as string[])

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-skin-text">Awards</h1>
        <p className="text-slate-500 mt-1">Growth, not glory.</p>
      </header>

      <div className="bg-gradient-to-br from-garden-50 to-garden-100 border border-garden-200 rounded-2xl p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-white text-garden-800 flex items-center justify-center">
            <Sparkles className="w-8 h-8" />
          </div>
          <div>
            <p className="text-sm text-garden-700">{currentMember?.name ?? 'You'} is</p>
            <h2 className="text-2xl font-semibold text-garden-900">Level {level} · {levelName}</h2>
            <p className="text-sm text-garden-700 mt-1">
              {ep} EP {nextLevelEp < 10000 ? `· ${Math.max(0, nextLevelEp - ep)} EP to next level` : '· Fully grown'}
            </p>
          </div>
        </div>
        <div className="mt-4 h-2 bg-garden-200 rounded-full overflow-hidden">
          <div className="h-full bg-garden-600 transition-all" style={{ width: `${Math.max(0, Math.min(100, progress))}%` }} />
        </div>
        {streakDays > 0 && (
          <p className="text-sm text-garden-800 mt-3">
            🔥 {streakDays}-day streak
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {ACHIEVEMENTS.map((a) => {
          const isEarned = earned.has(a.id) || ep >= a.ep
          return (
            <article
              key={a.id}
              className={`rounded-2xl p-4 border ${
                isEarned ? 'bg-garden-50 border-garden-200' : 'bg-slate-50 border-slate-200'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                  isEarned ? 'bg-garden-800 text-white' : 'bg-slate-200 text-slate-400'
                }`}>
                  <Award className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <h3 className={`text-sm font-medium ${isEarned ? 'text-garden-900' : 'text-slate-500'}`}>
                    {a.label}
                  </h3>
                  <p className={`text-xs mt-0.5 ${isEarned ? 'text-garden-700' : 'text-slate-400'}`}>
                    {a.description}
                  </p>
                </div>
              </div>
            </article>
          )
        })}
      </div>
    </div>
  )
}
